import React, { useEffect, useMemo } from 'react'
import { Outlet, useLocation, useNavigate } from 'react-router-dom'

import MRDrawer from '../../../components/MRDrawer'
import DesktopMRDrawer from '../../../components/DesktopMRDrawer'
import { useConnectedAppsStore } from '../../../stores/useConnectedAppsStore'
import { useTimeEntriesStore } from '../../../stores/useTimeEntriesStore'
import { useUserStore } from '../../../stores/useUserStore'
import { useWorkspaceUiStore } from '../../../stores/useWorkspaceUiStore'
import { useDrawer } from '../hooks/useDrawer'
import { ROUTER_PATH } from '../../router/config/constants'
import { getPageForPathname } from '../../router/utils/get-page-for-pathname'
import { useDeviceStore } from '../../../stores/useDeviceStore'
import { MRDrawerItems } from '../constants'
import AliveAppsNotificationBar from '../../connectedApps/components/AliveAppsNotificationBar'
import MiralinkAutoDeployBar from '@/features/connectedApps/components/MiralinkAutoDeployBar'
import { ADMIN_PORTAL_URL, getDrawerItemsForUser } from '../utils/adminPortal'
import {
  isTimelineDurationSessionPath,
  resetSessionSuggestedDurationFilter,
} from '@/features/timelineSidebar/utils/timelineSuggestedDuration'
import { ViewPreferenceGate } from './ViewPreferenceGate'
import { useSettingsUserId } from '@/features/settings/hooks/useSettingsUserId'

const WorkspaceShell = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const currentPage = getPageForPathname(location.pathname)
  const { expanded, setShowWelcome } = useWorkspaceUiStore()
  const { timeEntriesCount } = useTimeEntriesStore()
  const { isDelegationMode, selfUser } = useUserStore()
  const preferenceUserId = useSettingsUserId()
  const { setSupportEnabled } = useConnectedAppsStore()

  const isMobile = useDeviceStore(state => state.isMobile)

  useEffect(() => {
    if (!isTimelineDurationSessionPath(location.pathname)) {
      resetSessionSuggestedDurationFilter()
    }
  }, [location.pathname])

  const navigateToPage = useMemo(
    () => (page: string) => {
      if (page == 'tutorial') {
        setShowWelcome(true)
        return
      }
      if (page === 'adminPortal') {
        window.open(ADMIN_PORTAL_URL, '_blank', 'noopener,noreferrer')
        return
      }
      const route = ROUTER_PATH[page]
      if (location.pathname !== route) navigate(route)
    },
    [location.pathname, navigate]
  )

  const drawerItemsForUser = useMemo(
    () => getDrawerItemsForUser(MRDrawerItems, selfUser, isDelegationMode),
    [selfUser, isDelegationMode]
  )

  const { drawerItems, handleSelect, handleToggleDrawer } = useDrawer({
    items: drawerItemsForUser,
    navigateToPage,
  })

  const selectedId = useMemo(() => {
    const selectedItem = MRDrawerItems.find(item => item.text === currentPage)
    return selectedItem ? selectedItem.order : MRDrawerItems[0].order
  }, [currentPage])

  const workspaceContent = (
    <>
      <ViewPreferenceGate userId={preferenceUserId || undefined} />
      <Outlet />
    </>
  )

  if (isMobile) {
    return (
      <MRDrawer
        items={drawerItems}
        selectedId={selectedId}
        timeEntriesCount={timeEntriesCount}
        expanded={expanded}
        onSelect={handleSelect}
        handleToggleDrawer={handleToggleDrawer}
        onChatbotToggle={(enabled: boolean) => {
          setSupportEnabled(enabled)
        }}
      >
        <MiralinkAutoDeployBar />
        <div className="relative min-h-0 flex-1 overflow-hidden">
          {workspaceContent}
        </div>
      </MRDrawer>
    )
  }

  return (
    <DesktopMRDrawer
      items={drawerItems}
      selectedId={selectedId}
      timeEntriesCount={timeEntriesCount}
      onSelect={handleSelect}
      onChatbotToggle={(enabled: boolean) => {
        setSupportEnabled(enabled)
      }}
    >
      <AliveAppsNotificationBar />
      <MiralinkAutoDeployBar />
      <div className="flex min-h-0 flex-1 flex-col bg-white px-5">
        <div className="relative min-h-0 flex-1 overflow-hidden">
          {workspaceContent}
        </div>
      </div>
    </DesktopMRDrawer>
  )
}

export default WorkspaceShell
